/**
 * Role Constants and Utilities
 *
 * Access model:
 * 1) Company permission (allowPlanning, allowQC, etc.) — required for everyone, including superadmin.
 * 2) Role within module — superadmin gets all roles for modules the company subscribes to.
 */

import { hasCompanyPermissionFlag } from './companyPermissionFlags';

export const ROLE_NUMBERS = {
  ADMIN: 1,
  OPERATOR: 2,
  PLANNER: 3,
  QC_USER: 4,
  QC_ADMIN: 5,
  PLANT_COORDINATOR: 6,
  RECEIVING_USER: 7,
  FG_USER: 8,
  ENGINEERING: 9,
  ACCOUNTANT: 10,
  TOOLING: 11
};

export const ROLE_NAMES = {
  [ROLE_NUMBERS.ADMIN]: 'Admin',
  [ROLE_NUMBERS.OPERATOR]: 'Operator',
  [ROLE_NUMBERS.PLANNER]: 'Planner',
  [ROLE_NUMBERS.QC_USER]: 'QC User',
  [ROLE_NUMBERS.QC_ADMIN]: 'QC Admin',
  [ROLE_NUMBERS.PLANT_COORDINATOR]: 'Plant Coordinator',
  [ROLE_NUMBERS.RECEIVING_USER]: 'Receiving User',
  [ROLE_NUMBERS.FG_USER]: 'FG User',
  [ROLE_NUMBERS.ENGINEERING]: 'Engineering',
  [ROLE_NUMBERS.ACCOUNTANT]: 'Accountant',
  [ROLE_NUMBERS.TOOLING]: 'Tooling',
};

export const isSuperAdmin = (user) => {
  if (!user) return false;
  return user.is_superadmin === true || user.is_superadmin === 1;
};

export function userRoleNumber(user) {
  if (!user || user.role == null) return null;
  const r = user.role;
  return typeof r === 'string' ? parseInt(r, 10) : r;
}

/** Exact role match only (no superadmin implied). */
export function hasRoleNumber(user, roleNumber) {
  if (!user) return false;
  return userRoleNumber(user) === roleNumber;
}

/** Legacy helpers — include superadmin for system-wide UI (not module gates). */
export const isAdmin = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.ADMIN) || isSuperAdmin(user);
};

export const isPlanner = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.PLANNER) || isSuperAdmin(user);
};

export const isQCAdmin = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.QC_ADMIN) || isSuperAdmin(user);
};

export const isQCUser = (user) => {
  if (!user) return false;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.QC_USER) ||
    hasRoleNumber(user, ROLE_NUMBERS.QC_ADMIN) ||
    isSuperAdmin(user)
  );
};

export const isOperator = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.OPERATOR) || isSuperAdmin(user);
};

export const isPlantCoordinator = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.PLANT_COORDINATOR) || isSuperAdmin(user);
};

export const isReceivingUser = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.RECEIVING_USER) || isSuperAdmin(user);
};

export const isFGUser = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.FG_USER) || isSuperAdmin(user);
};

export const isEngineering = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.ENGINEERING) || isSuperAdmin(user);
};

export const isAccountant = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.ACCOUNTANT) || isSuperAdmin(user);
};

export const isTooling = (user) => {
  if (!user) return false;
  return hasRoleNumber(user, ROLE_NUMBERS.TOOLING) || isSuperAdmin(user);
};

/** Company subscribed to a module (superadmin must have flag too). */
export const hasPlanningModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowPlanning');

export const hasQCModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowQC');

export const hasInventoryModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowInventory');

export const hasEngineeringModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowEngineering');

export const hasToolingModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowTooling');

export const hasAccountingModuleAccess = (user) =>
  !!user && hasCompanyPermissionFlag(user, 'allowAccounting');

/** All roles within module when company has permission. */
export const canAccessPlanningModule = (user) => {
  if (!hasPlanningModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.PLANNER) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN)
  );
};

export const canAccessQCModule = (user) => {
  if (!hasQCModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.QC_ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.QC_USER) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN)
  );
};

export const canAccessEngineeringModule = (user) => {
  if (!hasEngineeringModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.ENGINEERING) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN)
  );
};

export const canAccessToolingModule = (user) =>
  hasToolingModuleAccess(user);

export const canAccessToolingManagement = (user) => {
  if (!hasToolingModuleAccess(user)) return false;
  return isSuperAdmin(user) || hasRoleNumber(user, ROLE_NUMBERS.TOOLING);
};

const INVENTORY_RECEIVING_ITEMS = new Set([
  'master-raw-material',
  'master-location',
  'activate-storage',
  'receiving-summary',
  'rm-transfer-summary',
  'scrapping-summary',
  'inventory-status-overview',
  'reports-all',
]);

const INVENTORY_FG_ITEMS = new Set([
  'master-finished-goods',
  'master-location',
  'activate-storage',
  'fg-transfer-summary',
  'fg-delivery-summary',
  'scrapping-summary',
  'inventory-status-overview',
  'reports-all',
]);

export const canAccessInventoryPortalItem = (user, itemKey) => {
  if (!hasInventoryModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  if (
    hasRoleNumber(user, ROLE_NUMBERS.PLANT_COORDINATOR) ||
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.ACCOUNTANT)
  ) {
    return true;
  }
  if (hasRoleNumber(user, ROLE_NUMBERS.RECEIVING_USER)) {
    return INVENTORY_RECEIVING_ITEMS.has(itemKey);
  }
  if (hasRoleNumber(user, ROLE_NUMBERS.FG_USER)) {
    return INVENTORY_FG_ITEMS.has(itemKey);
  }
  if (hasRoleNumber(user, ROLE_NUMBERS.OPERATOR)) {
    return INVENTORY_RECEIVING_ITEMS.has(itemKey) || INVENTORY_FG_ITEMS.has(itemKey);
  }
  return false;
};

export const canAccessAccounting = (user) => {
  if (!hasAccountingModuleAccess(user)) return false;
  if (isSuperAdmin(user)) return true;
  return (
    hasRoleNumber(user, ROLE_NUMBERS.ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.QC_ADMIN) ||
    hasRoleNumber(user, ROLE_NUMBERS.ACCOUNTANT)
  );
};

export const getRoleName = (roleNumber) => ROLE_NAMES[roleNumber] || 'Unknown';

export const hasRole = (user, roleNumber) => {
  if (!user) return false;
  return hasRoleNumber(user, roleNumber) || isSuperAdmin(user);
};
